import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BarChart3, CalendarDays, Eye, Plus } from 'lucide-react'
import { cn } from 'lib/utils'

export type OfferDetailPanel = 'details' | 'matching' | 'calendar' | 'validation'

interface OfferDetailPageHeaderProps {
  title: string
  applicationsCount: number
  activePanel: OfferDetailPanel | null
  onBack: () => void
  onOpenPanel: (panel: OfferDetailPanel) => void
  onAddHiringStep?: () => void
}

type HeaderPanelWithIcon = Exclude<OfferDetailPanel, 'validation'>

const PANEL_ICONS: Record<
  HeaderPanelWithIcon,
  React.ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  details: Eye,
  matching: BarChart3,
  calendar: CalendarDays,
}

const figmaBackButtonClass =
  'inline-flex h-[61px] w-[56px] shrink-0 items-center justify-center rounded-[10px] border border-[#D8D8D8] bg-white px-4 text-[#232323] transition-colors hover:bg-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted/50'

const figmaActionIconButtonClass =
  'inline-flex h-[61px] w-[56px] shrink-0 items-center justify-center rounded-[10px] border border-[#D8D8D8] bg-[#FAFAFA] px-4 text-[#232323] transition-colors hover:bg-[#F4F4F4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40 dark:border-border dark:bg-muted/40 dark:text-foreground dark:hover:bg-muted/60'

const figmaActionIconButtonActiveClass =
  'border-[#19A6A6] bg-[#E8F7F7] text-[#19A6A6] dark:border-[#19A6A6] dark:bg-[#19A6A6]/15 dark:text-[#7dd9d9]'

const figmaBackIconClass = 'h-6 w-6 shrink-0'
const figmaActionIconClass = 'relative h-[22px] w-[22px] shrink-0'

/**
 * Same layout model as Dashboard PageHeader + filter card:
 * full-width row, title left, actions right (lg:justify-between), stable on wide screens.
 */
export function OfferDetailPageHeader({
  title,
  applicationsCount,
  activePanel,
  onBack,
  onOpenPanel,
  onAddHiringStep,
}: OfferDetailPageHeaderProps) {
  const { t } = useTranslation()

  const panels: Array<{ id: HeaderPanelWithIcon; label: string }> = [
    { id: 'details', label: t('offerDetail.details') },
    { id: 'matching', label: t('aiMatching.matchingDetails', 'Matching details') },
    { id: 'calendar', label: t('offerDetail.calendarTab', 'Calendar') },
  ]

  return (
    <header className="flex w-full max-w-full min-w-0 flex-col gap-4">
      <div className="flex w-full min-w-0 items-start gap-4 lg:h-[61px] lg:items-center">
        <button
          type="button"
          onClick={onBack}
          className={figmaBackButtonClass}
          aria-label={t('offerDetail.back')}
          title={t('offerDetail.back')}
        >
          <ArrowLeft className={figmaBackIconClass} strokeWidth={1.5} />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-4 lg:gap-6">
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 overflow-hidden lg:max-w-[calc(100%-13.5rem)]">
              <p className="truncate text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-[#7F7F7F] dark:text-muted-foreground">
                {t('nav.offers', "Offres d'emploi")}
              </p>
              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                <h1
                  className="min-w-0 flex-1 truncate font-['Montserrat',sans-serif] text-[22px] font-bold leading-[1.2] tracking-normal text-foreground max-[1149px]:text-[20px] lg:text-[26px]"
                  style={{ fontWeight: 700 }}
                  title={title}
                >
                  {title}
                </h1>
                <span
                  className="inline-flex h-5 min-w-[28px] shrink-0 items-center justify-center rounded-full bg-[#19A6A6] px-2 py-px text-xs font-semibold leading-none text-white"
                  aria-label={t('offerDetail.applicationsCount', {
                    count: applicationsCount,
                    defaultValue: '{{count}} applications',
                  })}
                >
                  {applicationsCount}
                </span>
              </div>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-[10px]">
              {panels.map(({ id, label }) => {
                const Icon = PANEL_ICONS[id]
                const isActive = activePanel === id
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onOpenPanel(id)}
                    className={cn(
                      figmaActionIconButtonClass,
                      isActive && figmaActionIconButtonActiveClass
                    )}
                    aria-label={label}
                    aria-pressed={isActive}
                    title={label}
                  >
                    <Icon
                      className={figmaActionIconClass}
                      strokeWidth={1.5}
                    />
                  </button>
                )
              })}
              {onAddHiringStep && (
                <button
                  type="button"
                  onClick={onAddHiringStep}
                  className={figmaActionIconButtonClass}
                  aria-label={t('kanban.addHiringStep', 'Add hiring step')}
                  title={t('kanban.addHiringStep', 'Add hiring step')}
                >
                  <Plus className={figmaActionIconClass} strokeWidth={1.5} />
                </button>
              )}
            </div>
        </div>
      </div>
    </header>
  )
}
